import React, { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import AnalyticsFilters from './AnalyticsFilters';
import MediaCard from './MediaCard';
import MediaAnalyticsModal from './MediaAnalyticsModal';
import NoResults from '../../../_helpers/reusablefunctions/NoResults';
import {
  createDefaultAnalyticsFilters,
  fetchInstagramMediaAnalytics,
  resolveAnalyticsPayload,
  resolveAnalyticsRows,
} from '../../../_services/profile/instagramAnalytics.service';
import './analytics.css';

const MediaAnalytics = ({
  selectedHandleId = '',
  helperText,
  overrideMediaRows = null,
  hideFilters = false,
}) => {
  const [filters, setFilters] = useState(createDefaultAnalyticsFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mediaRows, setMediaRows] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleOpenMedia = (media) => {
    setSelectedMedia(media);
  };

  const handleCloseMedia = () => {
    setSelectedMedia(null);
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, handlerId: String(selectedHandleId || '') }));
  }, [selectedHandleId]);

  useEffect(() => {
    if (overrideMediaRows) {
      setMediaRows(Array.isArray(overrideMediaRows) ? overrideMediaRows : []);
      setError('');
      setLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadMediaAnalytics = async () => {
      if (!selectedHandleId) {
        setMediaRows([]);
        setError('');
        setLoading(false);
        return;
      }

      if (!filters.fromDate || !filters.toDate) {
        setError('Please select both dates.');
        setMediaRows([]);
        setLoading(false);
        return;
      }

      if (filters.fromDate > filters.toDate) {
        setError('From date cannot be after to date.');
        setMediaRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetchInstagramMediaAnalytics({
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          period: filters.period,
          handlerId: selectedHandleId,
        });

        if ((response?.status || 500) >= 400) {
          throw new Error('Unable to load media analytics.');
        }

        if (!isMounted) return;
        const payload = resolveAnalyticsPayload(response);
        setMediaRows(resolveAnalyticsRows(payload));
      } catch (fetchError) {
        if (!isMounted) return;
        setMediaRows([]);
        setError('Failed to fetch media analytics. Please try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMediaAnalytics();

    return () => {
      isMounted = false;
    };
  }, [filters.fromDate, filters.toDate, filters.period, selectedHandleId, overrideMediaRows]);

  return (
    <Box className="profile-analytics-panel">
      {!hideFilters ? (
        <AnalyticsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          helperText={helperText || 'Browse post-level analytics and open each card for more detail.'}
        />
      ) : helperText ? (
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ display: 'none' }} />
          <span className="profile-analytics-helper">{helperText}</span>
        </Box>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      <Box className="profile-media-grid">
        {(loading ? new Array(6).fill(null) : mediaRows).map((media, index) => (
          <MediaCard
            key={media?.id || media?.permalink || index}
            media={media}
            loading={loading}
            onClick={handleOpenMedia}
          />
        ))}
      </Box>

      {!loading && !error && !selectedHandleId ? (
        <NoResults message="Select a connected account to view media analytics." />
      ) : null}

      {!loading && !error && selectedHandleId && !mediaRows.length ? (
        <NoResults message="No media analytics found for the selected date range." />
      ) : null}

      <MediaAnalyticsModal open={Boolean(selectedMedia)} media={selectedMedia} onClose={handleCloseMedia} />
    </Box>
  );
};

export default MediaAnalytics;
